import Image from 'next/image'
import Link from 'next/link'
import { RichText } from '@payloadcms/richtext-lexical/react'
import type { Post } from '@/payload-types'
import { mediaUrl } from '@/lib/seo/jsonld'
import { imageCaption } from '@/lib/mediaCaption'
import { primaryCategory, postAuthor, resolvedTags } from '@/lib/relations'
import { formatDate, DATE_LONG } from '@/lib/format'
import { Breadcrumb } from './Breadcrumb'
import { PostMeta } from './PostMeta'
import { ShareButtons } from './ShareButtons'
import { articleConverters } from './RichTextConverters'
import SourcesBlock from './SourcesBlock'
import TagsBlock from './TagsBlock'
import Provenance from './Provenance'
import PracticalBox from './PracticalBox'
import AuthorCard from './AuthorCard'
import ArticlePolicy from './ArticlePolicy'
import { splitPracticalNodes } from '@/lib/practicalSection'

/**
 * Vista completa di un articolo (testata + corpo + tag + autore). ISOMORFA: nessun
 * import server-only, così lo stesso markup serve sia l'articolo SSR (page.tsx) sia
 * gli articoli caricati lato client dallo scroll infinito (<ArticleStream>).
 *
 * Il root <article> espone data-attribute (slug/href/title) letti dall'observer di
 * <ArticleStream> per cambiare URL + sparare il page_view GA4 quando l'articolo
 * entra al centro del viewport.
 */
export function ArticleView({
  post,
  siteName,
  siteUrl,
  articlePolicy,
  priority = false,
}: {
  post: Post
  siteName: string
  /** Origin assoluto del tenant (es. https://explorae.it) per i link di condivisione. */
  siteUrl?: string
  /**
   * Nota di policy della testata (`tenants.articlePolicy`), in coda al testo. Arriva come PROP e
   * non da un lookup qui dentro: questo componente è isomorfo — lo monta anche <ArticleStream> nel
   * browser — quindi il tenant lo risolve chi sta sul server (page.tsx, preview/[id]/page.tsx),
   * esattamente come `siteName` e `siteUrl`.
   */
  articlePolicy?: string | null
  priority?: boolean
}) {
  const author = postAuthor(post)
  const heroUrl = mediaUrl(post.featuredImage)
  const featured = post.featuredImage && typeof post.featuredImage === 'object' ? post.featuredImage : null
  const primary = primaryCategory(post)
  const date = formatDate(post.publishedAt, DATE_LONG)
  // Il filtro sugli slug vive in <TagsBlock>, che è anche chi decide se la sezione esiste.
  const tags = resolvedTags(post)
  // Coerente col template metadata del layout: «<title> · <siteName>».
  const docTitle = `${post.seo?.title || post.title} · ${siteName}`
  const shareUrl = `${siteUrl ?? ''}/${post.slug}/`
  // Sintesi in testata (sotto il titolo, sopra la byline — era il box TL;DR nel corpo):
  // usa il `tldr` se presente, altrimenti ripiega sull'`excerpt` (sintesi 1-2 frasi),
  // così compare anche sui contenuti legacy privi di tldr.
  const summary = post.tldr?.trim() || post.excerpt?.trim() || ''
  // Didascalia della copertina + dichiarazione d'uso: la regola è UNA e sta in imageCaption()
  // (src/lib/mediaCaption.ts), condivisa con le foto dentro il corpo.
  const heroCaption = imageCaption(featured)

  // La sezione «Informazioni pratiche» esce dal corpo e va nel suo riquadro (<PracticalBox>): dentro
  // `.prose` ereditava corpo 1.125rem, interlinea 1.8 e pallini in tinta d'accento, cioè era composta
  // come il racconto pur essendo una scheda di servizio.
  //
  // L'ALBERO SI RICOSTRUISCE SOLO SE C'È DAVVERO QUALCOSA DA STACCARE: 32 post su 35 del tenant non
  // hanno questa sezione, e per loro `content` è lo stesso oggetto di prima, non una copia con gli
  // stessi figli. Zero differenze nell'HTML servito, zero rischio di regressione su un articolo che
  // non c'entra niente con questa modifica.
  const { body, practical } = splitPracticalNodes(post.content?.root?.children)
  const bodyContent = practical ? { ...post.content, root: { ...post.content.root, children: body } } : post.content

  return (
    <article data-article-slug={post.slug} data-article-href={`/${post.slug}/`} data-article-title={docTitle}>
      {heroUrl ? (
        // Testata immersiva: l'altezza NON è più un aspect-ratio puro. Con un 21:9
        // (es. 2560×1080) il 2.4/1 valeva ~1066px e, sommato allo header sticky (h-16),
        // spingeva titolo/byline sotto la piega — l'utente vedeva solo l'immagine.
        // Quindi: altezza = min(altezza da aspect-ratio, 72svh), così su schermi bassi
        // e larghi la testata resta dentro il viewport. `min-h` (non `h`) perché il
        // blocco di testo — ora con TL;DR — può essere più alto dell'immagine su mobile:
        // in quel caso la testata cresce invece di sbordare fuori dal box.
        <header className="relative isolate flex min-h-[62vw] items-end overflow-hidden sm:min-h-[min(50vw,72svh)] lg:min-h-[min(41.7vw,72svh)]">
          <Image
            src={heroUrl}
            alt={featured?.alt || post.title}
            fill
            priority={priority}
            // Elemento LCP della pagina articolo: senza fetchpriority esplicito
            // Chrome lo accoda come Low dietro i chunk JS (vedi FeaturedMosaic).
            fetchPriority={priority ? 'high' : undefined}
            sizes="100vw"
            className="-z-10 object-cover"
          />
          {/* Scrim: da mobile il blocco di testo (titolo + sintesi + byline) occupa gran
              parte della testata, quindi la parte scura sale fino al 75%; da sm in su la
              testata è più alta del testo e il gradiente torna leggero per non spegnere
              la foto. */}
          <div className="absolute inset-0 -z-10 bg-gradient-to-t from-black/85 via-black/55 via-75% to-transparent sm:via-black/45 sm:via-50%" />
          {/* Stessa colonna del corpo (`mx-auto max-w-3xl` dentro `container-wide`): il blocco
              di testata stava sul bordo pieno del container mentre l'articolo parte ~300px più a
              destra, quindi titolo e primo paragrafo cadevano su due assi verticali diversi. La
              didascalia della copertina, subito sotto, era già incolonnata col corpo: l'hero era
              l'unico pezzo fuori squadra. Le vecchie max-w interne (4xl sul titolo, 3xl su sintesi
              e provenienza) sparisco: la misura ora la detta la colonna, una sola per tutti. */}
          <div className="container-wide w-full pb-8 pt-32 sm:pb-12">
            <div className="mx-auto max-w-3xl">
              {primary ? (
                <Link
                  href={`/categoria/${primary.slug}/`}
                  className="inline-block rounded-full bg-white/20 px-3 py-1 text-xs font-semibold uppercase tracking-wider text-white backdrop-blur-sm"
                >
                  {primary.name}
                </Link>
              ) : null}
              <h1 className="mt-3 text-3xl font-extrabold leading-[1.12] tracking-tight text-white sm:text-5xl">
                {post.title}
              </h1>
              {summary ? <p className="mt-4 leading-relaxed text-white/90 sm:text-lg">{summary}</p> : null}
              <PostMeta
                author={author}
                dateISO={post.publishedAt}
                dateLabel={date}
                className="mt-4 flex flex-wrap items-center gap-x-3 text-sm text-white/85"
              />
              {/* LA DICHIARAZIONE STAVA SOLO NEL RAMO SENZA COPERTINA, cioè non compariva su nessun
                  articolo generato — perché un articolo generato ha sempre una copertina. Misurato sulla
                  pagina pubblicata: zero occorrenze della striscia e zero della didascalia dell'immagine.
                  Su una pagina il cui testo è assistito da un modello e la cui immagine è INVENTATA da un
                  modello, la nota d'uso mancava esattamente nel caso per cui è stata scritta (art. 50
                  Reg. UE 2024/1689). */}
              <Provenance
                provenance={post.provenance}
                sourceCount={(post.sources ?? []).filter((s) => s?.url).length}
                className="mt-4 text-xs leading-relaxed text-white/75"
              />
            </div>
          </div>
        </header>
      ) : (
        <header className="container-wide pt-12 sm:pt-16">
          <div className="mx-auto max-w-3xl">
            {primary ? (
              <Link href={`/categoria/${primary.slug}/`} className="text-xs font-semibold uppercase tracking-wider text-accent-strong">
                {primary.name}
              </Link>
            ) : null}
            <h1 className="mt-2 text-3xl font-extrabold leading-tight tracking-tight text-ink sm:text-5xl">{post.title}</h1>
            {summary ? <p className="mt-4 leading-relaxed text-zinc-600 sm:text-lg">{summary}</p> : null}
            <PostMeta
              author={author}
              dateISO={post.publishedAt}
              dateLabel={date}
              className="mt-4 flex flex-wrap items-center gap-x-3 text-sm text-zinc-500"
            />
            <Provenance provenance={post.provenance} sourceCount={(post.sources ?? []).filter((s) => s?.url).length} />
          </div>
        </header>
      )}

      {/* Didascalia della copertina: `media.caption` e `media.aiGenerated` erano scritti dal worker,
          persistiti, e non renderizzati da nessuna riga — quindi un'immagine generata da un modello
          arrivava in pagina senza dire di esserlo. Su un luogo reale e nominato una copertina inventata
          è un'affermazione visiva, ed è la più difficile da smentire per chi legge: la dichiarazione va
          accanto all'immagine, non solo nei metadati del database. */}
      {heroUrl && heroCaption ? (
        <div className="container-wide">
          <p className="mx-auto mt-3 max-w-3xl text-xs text-zinc-500">{heroCaption}</p>
        </div>
      ) : null}

      <div className="container-wide">
        <div className="mx-auto max-w-3xl py-10 sm:py-14">
          <Breadcrumb
            className="mb-6"
            items={[
              { name: 'Home', href: '/' },
              ...(primary ? [{ name: primary.name, href: `/categoria/${primary.slug}/` }] : []),
              { name: post.title },
            ]}
          />

          <ShareButtons url={shareUrl} title={post.title} className="mb-8" />

          {/* UNA SOLA CHIAMATA A <RichText>, come prima: il riquadro non passa da qui, quindi in
              `.prose` resta un solo <div class="payload-richtext"> e la struttura del sottoalbero è
              identica a quella di oggi. Verificato di proposito, perché è il punto in cui questa
              modifica poteva cambiare l'aspetto di TUTTI gli articoli: la regola «lead» di
              globals.css (`.prose > :where(p):first-of-type`, primo capoverso più grande e scuro) è
              già inerte da quando <RichText> inserisce il suo <div> — il <p> è nipote di `.prose`,
              non figlio. Verificato sull'HTML servito: `<div class="prose ..."><div
              class="payload-richtext"><p>`. Il primo capoverso continua a vedersi come oggi.
              ATTENZIONE a cosa la riaccenderebbe, che NON è una seconda chiamata a <RichText> (ogni
              chiamata porta il suo <div>, e di `p` figli di `.prose` continuano a non essercene): la
              riaccende solo un <p> figlio DIRETTO di `.prose` — togliere quel <div>, o scrivere un
              capoverso a mano qui dentro accanto a <RichText>. */}
          {/* NIENTE SILLABAZIONE AUTOMATICA, E QUINDI NIENTE GIUSTIFICATO (05/08/2026).
              `hyphens-auto` faceva sillabare il browser, e sull'articolo di Bomarzo ha stampato in
              pagina «San-» a fine riga e «t'Antonio» all'inizio della successiva. Il testo servito è
              giusto (`Sant&#x27;Antonio` nell'HTML, `lang="it"` sull'<html>): il taglio lo decide il
              motore di sillabazione, che tratta l'apostrofo come una lettera qualunque e spezza
              «Sant'Antonio» dopo tre caratteri. È un errore che nessuna correzione a monte può
              prevenire — in italiano l'apostrofo sta in l', d', un', dell', all', Sant' — e in pagina
              si legge come un errore di ortografia nostro.
              IL GIUSTIFICATO VA VIA CON LEI, e non è un ripensamento estetico: giustificare senza
              sillabare allarga gli spazi fra le parole, e su una colonna da telefono (35 caratteri per
              riga, con parole italiane lunghe) i buchi bianchi sono più brutti del bordo irregolare.
              Il modello di riferimento del registro (travel365.it) scrive allineato a sinistra.
              Se si volesse rimettere il giustificato, la sillabazione NON deve tornare con lui. */}
          <div className="prose prose-zinc max-w-none prose-headings:text-left prose-headings:tracking-tight prose-a:font-medium">
            <RichText data={bodyContent as never} converters={articleConverters} />
          </div>

          {/* LA CODA DELL'ARTICOLO È UNA SEQUENZA, E IL RITMO LO TIENE IL CONTENITORE.
              Ogni blocco si portava il suo margine, e il risultato erano tre distanze diverse fra
              quattro blocchi che si leggono di fila: 80px fra riquadro pratico e tag, 44 fra tag e
              fonti, 48 fra fonti e firma. Nessuna delle tre era sbagliata da sola; è la sequenza che
              non tornava. Qui la distanza è UNA (`space-y-6`), e il margine non compare più dentro
              i quattro componenti.
              È il contenitore a doverla tenere anche per una ragione che i blocchi non possono
              risolvere: `space-y` spazia gli elementi CHE ESISTONO, e <PracticalBox>, <TagsBlock> e
              <AuthorCard> non rendono nulla quando l'articolo non ha quella roba. Col margine sul
              singolo blocco, il primo che sopravvive si porta dietro lo stacco pensato per stare
              sotto un altro — che è il bug che c'era prima dietro il `tags.length ? …`. */}
          <div className="mt-12 space-y-6">
            {/* Fra il corpo e le fonti: è ancora l'articolo (le sue parole, il suo titolo di sezione),
                ma già informazione di servizio — che è esattamente il posto che occupa nella pagina. */}
            {practical ? <PracticalBox nodes={practical} /> : null}

            {/* LA NOTA DELLA TESTATA CHIUDE IL TESTO, e sta qui e non in fondo alla coda: qualifica
                l'articolo che si è appena letto — che cos'è, e che cosa non è — quindi va attaccata al
                testo. Sotto le fonti e la firma sarebbe il piede della pagina, cioè la riga che si
                salta. Compare solo se il sito ne ha una (vedi <ArticlePolicy>). */}
            <ArticlePolicy text={articlePolicy} />

            {/* I TAG PRIMA DELLE FONTI: sono navigazione (portano dentro il sito), le fonti sono la
                chiusura documentaria (portano fuori, con `nofollow`). Chi finisce di leggere incontra
                prima il «dove vado adesso» e poi il «da dove viene questo». Stavano sotto, e sotto
                alle fonti erano l'ultima cosa dell'articolo prima della firma: una fila di `#parole`
                senza titolo né filetto, in coda a un blocco che invece si presentava. */}
            <TagsBlock tags={tags} />

            <SourcesBlock sources={post.sources} />

            {/* La firma chiude la pagina: la testata immersiva scrolla via, quindi è l'unico punto in
                cui il lettore ritrova «chi» e «quando» senza risalire in cima — e ora anche la faccia
                di chi ha revisionato il pezzo. */}
            {author ? (
              <AuthorCard author={author} publishedAt={post.publishedAt} contentUpdatedAt={post.contentUpdatedAt} />
            ) : null}
          </div>
        </div>
      </div>
    </article>
  )
}
